/**
 * Examples Page
 *
 * Page wrapper for the EnergyPlus Example Library.
 */

import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExampleLibrary } from '@/examples';
import type { ExampleFileMetadata } from '@/examples';
import { useFileStore } from '@/storage';

export function ExamplesPage() {
  const navigate = useNavigate();
  const loadExampleModel = useFileStore((state) => state.loadExampleModel);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSelect = useCallback(
    async (example: ExampleFileMetadata) => {
      try {
        setError(null);
        setIsLoading(true);
        // Load the example file
        await loadExampleModel(example.idfUrl, example.name);
        // Navigate to the editor
        navigate('/editor');
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load example';
        setError(message);
        console.error('Failed to load example:', err);
      } finally {
        setIsLoading(false);
      }
    },
    [loadExampleModel, navigate]
  );

  return (
    <div className="h-full relative">
      {/* Error message */}
      {error && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 shadow-lg dark:border-red-800 dark:bg-red-950">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-600 dark:text-red-400">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span className="text-sm text-red-700 dark:text-red-400">{error}</span>
          <button
            onClick={() => { setError(null); }}
            className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}

      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-background/50">
          <div className="flex items-center gap-3 rounded-lg border bg-card px-6 py-4 shadow-lg">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <span className="text-sm">Loading example...</span>
          </div>
        </div>
      )}

      <ExampleLibrary onSelect={handleSelect} />
    </div>
  );
}
